var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};

// src/constants.ts
var EXPERIMENT_OVERRIDE_STORAGE_PREFIX = "acorns_toggles_experiment_override:";
var EXPERIMENT_VARIABLES_OVERRIDE_STORAGE_PREFIX = "acorns_toggles_experiment_variables_override:";
var FEATURE_OVERRIDE_STORAGE_PREFIX = "acorns_toggles_feature_override:";
var FEATURE_VARIABLES_OVERRIDE_STORAGE_PREFIX = "acorns_toggles_feature_variables_override:";

// src/utils/amplitude-experiment-react.tsx
import { Experiment, ExperimentClient } from "@amplitude/experiment-js-client";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";

// src/utils/amplitude-constants.ts
var AmplitudeExperimentEvaluationModes = {
  Local: "local"
};

// src/utils/amplitude-experiment-react.tsx
var AmplitudeExperimentClientContext = createContext(
  null
);
var AmplitudeExperimentInitializationErrorContext = createContext(null);
function useAmplitudeExperimentClient() {
  const client = useContext(AmplitudeExperimentClientContext);
  return client;
}
function AmplitudeExperimentProvider(props) {
  const [amplitudeExperimentClient, setAmplitudeExperimentClient] = useState(null);
  const [
    amplitudeExperimentInitializationError,
    setAmplitudeExperimentInitializationError
  ] = useState(null);
  useEffect(() => {
    if (amplitudeExperimentClient === null && props.deploymentKey) {
      const client = Experiment.initialize(props.deploymentKey, {
        exposureTrackingProvider: props.exposureTrackingProvider,
        fetchOnStart: false,
        // We can skip this given we are currently solely using local evaluation (instead of remote evaluation).
        flagsServerUrl: props.flagsServerUrl,
        initialFlags: props.initialFlags,
        pollOnStart: false,
        // Disable this since we do not want toggles states to change mid-session.
        retryFetchOnFailure: false,
        // Disable this since we do not want toggles states to change mid-session.
        serverUrl: props.serverUrl,
        fetchTimeoutMillis: props.fetchTimeoutMillis,
        userProvider: props.userProvider
      });
      client.start(props.user).then(() => {
        setAmplitudeExperimentClient(client);
      }).catch((error) => {
        console.error("Error initializing toggles SDK: " + error);
        setAmplitudeExperimentInitializationError(error);
        setAmplitudeExperimentClient(client);
      });
    }
  }, [
    amplitudeExperimentClient,
    props.deploymentKey,
    props.fetchTimeoutMillis,
    props.flagsServerUrl,
    props.initialFlags,
    props.serverUrl,
    props.user,
    props.exposureTrackingProvider,
    props.userProvider
  ]);
  useEffect(() => {
    if (amplitudeExperimentClient && props.user) {
      amplitudeExperimentClient.setUser(props.user);
    }
  }, [amplitudeExperimentClient, props.user]);
  if (props.waitForInitialization === true && amplitudeExperimentClient === null) {
    const WaitForInitializationLoadingComponent = props.waitForInitializationLoadingComponent;
    if (WaitForInitializationLoadingComponent) {
      return /* @__PURE__ */ React.createElement(WaitForInitializationLoadingComponent, null);
    }
    return null;
  }
  return /* @__PURE__ */ React.createElement(
    AmplitudeExperimentClientContext.Provider,
    {
      value: amplitudeExperimentClient
    },
    /* @__PURE__ */ React.createElement(
      AmplitudeExperimentInitializationErrorContext.Provider,
      {
        value: amplitudeExperimentInitializationError
      },
      props.children
    )
  );
}
function MockAmplitudeExperimentProvider({
  children
}) {
  const [amplitudeExperimentClient, setAmplitudeExperimentClient] = useState(null);
  useEffect(() => {
    if (amplitudeExperimentClient === null) {
      const client = new ExperimentClient("", {});
      setAmplitudeExperimentClient(client);
    }
  }, [amplitudeExperimentClient]);
  return /* @__PURE__ */ React.createElement(
    AmplitudeExperimentClientContext.Provider,
    {
      value: amplitudeExperimentClient
    },
    children
  );
}
function useAmplitudeExperimentFeature(featureKey) {
  var _a;
  const amplitudeExperimentClient = useAmplitudeExperimentClient();
  if (amplitudeExperimentClient === null) {
    return [false, {}];
  }
  const variant = amplitudeExperimentClient.variant(featureKey);
  if (variant.metadata && variant.metadata.evaluationMode !== AmplitudeExperimentEvaluationModes.Local) {
    console.error(
      `The feature '${featureKey}' does not have the expected Evaluation Mode, which must be set to Local. This can be updated on the feature's dashboard (Settings -> Advanced). Returning feature as disabled.`
    );
    return [false, {}];
  }
  return [variant.value === "on", (_a = variant.payload) != null ? _a : {}];
}
function useAmplitudeExperimentExperiment(experimentKey) {
  var _a;
  const amplitudeExperimentClient = useAmplitudeExperimentClient();
  if (amplitudeExperimentClient === null) {
    return [void 0, {}];
  }
  const variant = amplitudeExperimentClient.variant(experimentKey);
  if (variant.metadata && variant.metadata.evaluationMode !== AmplitudeExperimentEvaluationModes.Local) {
    console.error(
      `The experiment '${experimentKey}' does not have the expected Evaluation Mode, which must be set to Local. This can be updated on the experiment's dashboard (Settings -> Delivery Settings). Returning experiment with no variant.`
    );
    return [void 0, {}];
  }
  return [variant.value, (_a = variant.payload) != null ? _a : {}];
}
function useAmplitudeExperimentGetAllEnabledFeatures() {
  const amplitudeExperimentClient = useAmplitudeExperimentClient();
  const getAllEnabledFeatures = useCallback(
    function() {
      if (amplitudeExperimentClient === null) {
        return [];
      }
      const allVariants = amplitudeExperimentClient.all();
      const allEnabledFeatures = Object.entries(allVariants).filter(([_key, variant]) => {
        return variant.value === "on";
      }).map(([key, _variant]) => {
        return key;
      });
      return allEnabledFeatures;
    },
    [amplitudeExperimentClient]
  );
  return getAllEnabledFeatures;
}
function useAmplitudeExperimentInitializationError() {
  const initializationError = useContext(
    AmplitudeExperimentInitializationErrorContext
  );
  return initializationError;
}

// src/MockTogglesProvider.tsx
import React3 from "react";

// src/TogglesProvider.tsx
import React2, { createContext as createContext2 } from "react";
var TogglesConfigContext = createContext2({
  allowToggleOverrides: false
});
function TogglesProvider({
  children,
  allowToggleOverrides = false,
  amplitudeExperimentExposureTrackingProvider,
  amplitudeExperimentFlagsServerUrl,
  amplitudeExperimentInitialFlagsJson,
  amplitudeExperimentUserProvider,
  amplitudeExperimentServerUrl,
  fetchTogglesTimeout = 1e4,
  sdkKey,
  userAttributes = {},
  waitForInitialization,
  waitForInitializationLoadingComponent
}) {
  const amplitudeExperimentUser = {
    user_id: userAttributes.userId,
    device_id: userAttributes.deviceId
  };
  return /* @__PURE__ */ React2.createElement(TogglesConfigContext.Provider, { value: { allowToggleOverrides } }, /* @__PURE__ */ React2.createElement(
    AmplitudeExperimentProvider,
    {
      deploymentKey: sdkKey,
      exposureTrackingProvider: amplitudeExperimentExposureTrackingProvider,
      fetchTimeoutMillis: fetchTogglesTimeout,
      flagsServerUrl: amplitudeExperimentFlagsServerUrl,
      initialFlags: amplitudeExperimentInitialFlagsJson,
      serverUrl: amplitudeExperimentServerUrl,
      user: amplitudeExperimentUser,
      userProvider: amplitudeExperimentUserProvider,
      waitForInitialization,
      waitForInitializationLoadingComponent
    },
    children
  ));
}

// src/MockTogglesProvider.tsx
function MockTogglesProvider({
  children
}) {
  return /* @__PURE__ */ React3.createElement(TogglesConfigContext.Provider, { value: { allowToggleOverrides: true } }, /* @__PURE__ */ React3.createElement(MockAmplitudeExperimentProvider, null, children));
}

// src/useExperiment.ts
import { useContext as useContext2 } from "react";
function useExperiment(key) {
  var _a, _b;
  const [variant, variables] = useAmplitudeExperimentExperiment(key);
  const { allowToggleOverrides } = useContext2(TogglesConfigContext);
  const override = (_a = window.sessionStorage.getItem(EXPERIMENT_OVERRIDE_STORAGE_PREFIX + key)) != null ? _a : window.localStorage.getItem(EXPERIMENT_OVERRIDE_STORAGE_PREFIX + key);
  if (allowToggleOverrides && override) {
    console.info(
      `Experiment override detected for '${key}'. Overriding with variant '${override}'.`
    );
    const variablesOverride = (_b = window.sessionStorage.getItem(
      EXPERIMENT_VARIABLES_OVERRIDE_STORAGE_PREFIX + key
    )) != null ? _b : window.localStorage.getItem(
      EXPERIMENT_VARIABLES_OVERRIDE_STORAGE_PREFIX + key
    );
    if (variablesOverride) {
      console.info(
        `Experiment variables override detected for '${key}'. Overriding with variables:`,
        JSON.parse(variablesOverride)
      );
      return [override, JSON.parse(variablesOverride)];
    }
    return [override, variables];
  }
  return [variant, variables];
}

// src/useFeature.ts
import { useContext as useContext3 } from "react";
function useFeature(key) {
  var _a, _b;
  const [isEnabled, variables] = useAmplitudeExperimentFeature(key);
  const { allowToggleOverrides } = useContext3(TogglesConfigContext);
  const override = (_a = window.sessionStorage.getItem(FEATURE_OVERRIDE_STORAGE_PREFIX + key)) != null ? _a : window.localStorage.getItem(FEATURE_OVERRIDE_STORAGE_PREFIX + key);
  if (allowToggleOverrides && override) {
    console.info(
      `Feature override detected for '${key}'. Overriding feature as ${override === "true" ? "ENABLED" : "DISABLED"}.`
    );
    const variablesOverride = (_b = window.sessionStorage.getItem(
      FEATURE_VARIABLES_OVERRIDE_STORAGE_PREFIX + key
    )) != null ? _b : window.localStorage.getItem(
      FEATURE_VARIABLES_OVERRIDE_STORAGE_PREFIX + key
    );
    if (variablesOverride) {
      console.info(
        `Feature variables override detected for '${key}'. Overriding with variables:`,
        JSON.parse(variablesOverride)
      );
      return [override === "true", JSON.parse(variablesOverride)];
    }
    return [override === "true", variables];
  }
  return [isEnabled, variables];
}

// src/useTogglesStatus.ts
function useTogglesStatus() {
  const amplitudeExperimentClient = useAmplitudeExperimentClient();
  const initializationError = useAmplitudeExperimentInitializationError();
  const getAllEnabledFeatures = useAmplitudeExperimentGetAllEnabledFeatures();
  return {
    togglesReady: amplitudeExperimentClient !== null,
    initializationError: initializationError ? `${initializationError}` : null,
    getAllEnabledFeatures
  };
}

// src/withToggles.tsx
import React4, { useCallback as useCallback2, useContext as useContext4 } from "react";
function withToggles(WrappedComponent) {
  return function TogglesEnhancedComponent(props) {
    const amplitudeExperimentClient = useAmplitudeExperimentClient();
    const { allowToggleOverrides } = useContext4(TogglesConfigContext);
    const isFeatureEnabled = useCallback2(
      function(featureKey) {
        var _a;
        if (amplitudeExperimentClient === null) {
          return false;
        }
        const variant = amplitudeExperimentClient.variant(featureKey);
        if (variant.metadata && variant.metadata.evaluationMode !== AmplitudeExperimentEvaluationModes.Local) {
          console.error(
            `The feature '${featureKey}' does not have the expected Evaluation Mode, which must be set to Local. This can be updated on the feature's dashboard (Settings -> Advanced). Returning feature as disabled.`
          );
          return false;
        }
        const override = (_a = window.sessionStorage.getItem(
          FEATURE_OVERRIDE_STORAGE_PREFIX + featureKey
        )) != null ? _a : window.localStorage.getItem(
          FEATURE_OVERRIDE_STORAGE_PREFIX + featureKey
        );
        if (allowToggleOverrides && override) {
          console.info(
            `Feature override detected for '${featureKey}'. Overriding feature as ${override === "true" ? "ENABLED" : "DISABLED"}.`
          );
          return override === "true";
        }
        return variant.value === "on";
      },
      [amplitudeExperimentClient, allowToggleOverrides]
    );
    const getVariation = useCallback2(
      function(experimentKey) {
        var _a;
        if (amplitudeExperimentClient === null) {
          return void 0;
        }
        const variant = amplitudeExperimentClient.variant(experimentKey);
        if (variant.metadata && variant.metadata.evaluationMode !== AmplitudeExperimentEvaluationModes.Local) {
          console.error(
            `The experiment '${experimentKey}' does not have the expected Evaluation Mode, which must be set to Local. This can be updated on the experiment's dashboard (Settings -> Delivery Settings). Returning experiment with no variant.`
          );
          return void 0;
        }
        const override = (_a = window.sessionStorage.getItem(
          EXPERIMENT_OVERRIDE_STORAGE_PREFIX + experimentKey
        )) != null ? _a : window.localStorage.getItem(
          EXPERIMENT_OVERRIDE_STORAGE_PREFIX + experimentKey
        );
        if (allowToggleOverrides && override) {
          console.info(
            `Experiment override detected for '${experimentKey}'. Overriding with variant '${override}'.`
          );
          return override;
        }
        return variant.value;
      },
      [amplitudeExperimentClient, allowToggleOverrides]
    );
    const { togglesReady, getAllEnabledFeatures } = useTogglesStatus();
    return /* @__PURE__ */ React4.createElement(
      WrappedComponent,
      __spreadValues({
        toggles: {
          isFeatureEnabled,
          getVariation,
          togglesReady,
          getAllEnabledFeatures
        }
      }, props)
    );
  };
}
export {
  EXPERIMENT_OVERRIDE_STORAGE_PREFIX,
  EXPERIMENT_VARIABLES_OVERRIDE_STORAGE_PREFIX,
  FEATURE_OVERRIDE_STORAGE_PREFIX,
  FEATURE_VARIABLES_OVERRIDE_STORAGE_PREFIX,
  MockTogglesProvider,
  TogglesProvider,
  useExperiment,
  useFeature,
  useTogglesStatus,
  withToggles
};
